"use client";

import dynamic from "next/dynamic";
import { Suspense, useLayoutEffect, useRef, type ReactNode } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import type { KundaliResponse } from "@/lib/api/kundali";
import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";
import type { KundaliLang, KundaliLocale } from "./KundaliFormCard.i18n";

const KundaliResult = dynamic(() => import("@/components/KundaliResult"), { ssr: false });

export function KundaliResultBoundary({
  result,
  language,
  locale = language,
  onBack,
  layout,
  postResultAdjuncts,
}: {
  result: KundaliResponse;
  language: KundaliLang;
  /** The language the chart is drawn in; defaults to `language`. */
  locale?: KundaliLocale;
  onBack: () => void;
  layout: "result-first" | "chat-first";
  postResultAdjuncts: ReactNode;
}) {
  return (
    <ErrorBoundary boundary="kundali_result">
      {/* Floor the ENTIRE swap region at min-h-screen, not just the loading
          skeleton (rule 58). The skeleton reserves min-h-screen, but a SHORT
          result (an error/degenerate chart, or the deterministic CLS-test stub)
          is shorter than that — so on commit the region shrank ~screen→~400px and
          pulled the below-fold sibling content (the AIAstrologerCTA bridge + cv-auto
          SEO block) UP into the viewport, scoring a large post-submit shift. In
          production a full chart is TALLER than the screen so the region grows
          below the fold (no shift) — which is why this was cold/degenerate-only.
          Flooring the wrapper keeps the region ≥ screen through skeleton AND
          result, so the below-fold content never rises into the shift-scored
          viewport regardless of result height. */}
      <div className="min-h-screen">
        <Suspense fallback={<KundaliResultLoadingSkeleton layout={layout} />}>
          <KundaliResult
            result={result}
            language={language}
            displayLocale={locale}
            onBack={onBack}
            layout={layout}
            postResultAdjuncts={postResultAdjuncts}
            anchorOnMount
          />
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

// Reserved-space placeholder shown for the (usually sub-frame, thanks to the
// preload) window between the form unmounting and the result chunk committing.
// Two jobs, both load-bearing for CLS:
//   1. Reserve a full viewport of height so the swap never collapses to 0px —
//      the `fallback={null}` gap is what let the result pop 0px->full-height and
//      shove the always-present SEO content below it (CrUX p75 CLS ≈ 1.0 mobile
//      on /free-kundali/, even though on-load CLS is clean).
//   2. Anchor this swap container to the top of the viewport BEFORE paint (under
//      the sticky chrome via BELOW_STICKY_CHROME_SCROLL_MARGIN), so the
//      skeleton->result growth happens below the fold and never enters the
//      viewport's layout-shift accounting. KundaliResult re-applies the same
//      anchor once committed.
function KundaliResultLoadingSkeleton({ layout }: { layout: "result-first" | "chat-first" }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    // Anchor the cold-path skeleton frame to the SAME position the committed
    // KundaliResult will use, so there is no scroll delta between the two frames.
    // A mismatch (skeleton at its own top ≈ scrollY 310 vs result at top:0) once
    // displaced the sibling agent-bridge / cv-auto SEO sections into the
    // shift-scored viewport (CLS ~0.4-0.5, cold only). The form container the
    // swap replaces (finishKundali's pre-commit anchor), this skeleton, the
    // result-first region and the chat-first chat panel all anchor with
    // BELOW_STICKY_CHROME_SCROLL_MARGIN (C12). The skeleton and the result are
    // the first child of the same min-h-screen wrapper, so all four land on
    // the same scrollY for both layouts (rule 58).
    ref.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
  }, [layout]);
  return (
    <div
      ref={ref}
      aria-hidden
      className={`mx-auto max-w-3xl min-h-screen px-4 py-8 ${BELOW_STICKY_CHROME_SCROLL_MARGIN}`}
    />
  );
}
